import './DatacMessage.less'

import { App } from 'antd'
import classNames from 'classnames'
import dayjs from 'dayjs'
import React from 'react'

import { DatacIcon, DatacIconProps } from '../../components'
import { InformationBanner, InformationBannerType, NextRelease } from '../../requests'
import { getErrorMessage } from './errorMessages'

const DEFAULT_DURATION = 4

interface DatacMessageContentProps {
  title: string | React.ReactNode
  body?: string | React.ReactNode
  icon: DatacIconProps['name']
}

const MessageContent: React.FC<DatacMessageContentProps> = ({ title, body, icon }) => {
  return (
    <div className={classNames('datac-message-content', { 'datac-message-content--title-only': !body })}>
      <DatacIcon size="big" name={icon} type="white" />
      <div className="datac-message-content__text">
        <div className="datac-message-content__title">{title}</div>
        <div className="datac-message-content__body">{body}</div>
      </div>
    </div>
  )
}

const closeIcon = <DatacIcon name="x" type="white-on-semi-transparent" />

export type scopedIntlType = (id: string, variables?: Record<string, string>) => string

export const useDatacMessage = () => {
  const { notification } = App.useApp()
  return {
    error: (title: string | React.ReactNode, body?: string | React.ReactNode, duration?: number) =>
      notification.error({
        message: <MessageContent title={title} body={body} icon="xSquare" />,
        className: 'ant-notification-notice-error',
        duration: duration || DEFAULT_DURATION,
        icon: null,
        closeIcon,
      }),
    success: (title: string | React.ReactNode, body?: string | React.ReactNode, duration?: number) =>
      notification.success({
        message: <MessageContent title={title} body={body} icon="check" />,
        className: 'ant-notification-notice-success',
        duration: duration || DEFAULT_DURATION,
        icon: null,
        closeIcon,
      }),
    warning: (title: string | React.ReactNode, body?: string | React.ReactNode, duration?: number) =>
      notification.warning({
        message: <MessageContent title={title} body={body} icon="alertTriangle" />,
        className: 'ant-notification-notice-warning',
        duration: duration || DEFAULT_DURATION,
        icon: null,
        closeIcon,
      }),
    genericError: (intl: scopedIntlType, code?: number) =>
      notification.error({
        message: <DatacGenericError intl={intl} code={code} />,
        className: 'ant-notification-notice-error',
        duration: DEFAULT_DURATION,
        icon: null,
        closeIcon,
      }),
    nextReleaseInfo: (
      intl: scopedIntlType,
      nextRelease: NextRelease,
      onClose: (code: string) => void,
      language: string,
    ) =>
      notification.info({
        message: <DatacNextReleaseInfo intl={intl} nextRelease={nextRelease} language={language} />,
        className: 'ant-notification-notice-info',
        icon: null,
        duration: null,
        closeIcon,
        onClose: () => onClose(nextRelease.code),
        key: 'next-release-info',
        placement: 'bottomRight',
      }),
    informationBanner: (informationBanner: InformationBanner, onClose: (token: string) => void) =>
      notification.open({
        message: <DatacInformationBanner informationBanner={informationBanner} />,
        className: classNames('information-banner', {
          'information-banner--error': InformationBannerType.Error,
          'information-banner--information': InformationBannerType.Information,
          'information-banner--warning': InformationBannerType.Warning,
        }),
        icon: null,
        duration: null,
        closeIcon,
        onClose: () => onClose(informationBanner.token),
        key: 'information-banner',
        placement: 'top',
      }),
  }
}

const DatacGenericError: React.FC<{ intl: scopedIntlType; code: number }> = ({ intl, code }) => (
  <div className="datac-message-content">
    <DatacIcon size="big" name="xSquare" type="white" />
    <div className="datac-message-content__text">
      <div className="datac-message-content__title">{intl('error.executing_request.title')}</div>
      <div className="datac-message-content__body">
        {getErrorMessage(code, intl)}
        {code >= 1000 && (
          <div className="datac-message-content__body__error-code">
            {intl('common.error')} {code}
          </div>
        )}
      </div>
    </div>
  </div>
)

const DatacNextReleaseInfo: React.FC<{ intl: scopedIntlType; nextRelease: NextRelease; language: string }> = ({
  intl,
  nextRelease,
  language,
}) => {
  const date = nextRelease.date && dayjs(nextRelease.date).locale(language).format('MMMM Do, YYYY')
  return (
    <div className="datac-message-content">
      <DatacIcon size="big" name="calendar" type="blue" />
      <div className="datac-message-content__text datac-message-content__text--release-info">
        <div className="datac-message-content__title">{intl('next_release_notes.title', { date })}</div>
        <div>
          {intl('next_release_notes.1', {
            code: nextRelease.code,
            date,
            time: nextRelease.time,
            timezone: nextRelease.timezone,
          })}
        </div>
        <a href={nextRelease.notesUrl} target="_blank" rel="noreferrer">
          {intl('next_release_notes.2')}
        </a>
      </div>
    </div>
  )
}

const DatacInformationBanner: React.FC<{
  informationBanner: InformationBanner
}> = ({ informationBanner }) => {
  return (
    <div className="datac-message-content">
      <DatacIcon size="small" name="calendar" />
      <div className="datac-message-content__text datac-message-content__text--information-banner">
        <div>{informationBanner.text}</div>
        {informationBanner.link && (
          <a href={informationBanner.link} target="_blank" rel="noreferrer">
            {informationBanner.linkName}
          </a>
        )}
      </div>
    </div>
  )
}
